/**
 * Validate Frontend Routes Tool
 *
 * Validates synchronization between backend NavRoute attributes and frontend routing.
 * Checks for orphaned clients, missing routes, and permission mismatches.
 */

import { Tool } from '@modelcontextprotocol/sdk/types.js';
import path from 'path';

import {
  Config,
  ValidateFrontendRoutesInputSchema,
  ValidateFrontendRoutesInput,
  FrontendRouteValidationResult,
  NavRouteEntry
} from '../types/index.js';
import { logger } from '../lib/logger.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { readText, fileExists } from '../utils/fs.js';
import { glob } from 'glob';

export const validateFrontendRoutesTool: Tool = {
  name: 'validate_frontend_routes',
  description: `Validate frontend routes against backend NavRoute attributes.

Checks:
- navRoutes.generated.ts exists and is up-to-date
- API clients use correct NavRoute paths
- React Router configuration matches backend routes
- Permission configurations are synchronized

Example:
  validate_frontend_routes scope="all"

Reports issues and provides actionable recommendations for synchronization.`,
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Path to SmartStack project root (defaults to configured project path)',
      },
      scope: {
        type: 'string',
        enum: ['api-clients', 'routes', 'registry', 'all'],
        default: 'all',
        description: 'Scope of validation',
      },
      options: {
        type: 'object',
        properties: {
          fix: { type: 'boolean', default: false, description: 'Auto-fix minor issues' },
          strict: { type: 'boolean', default: false, description: 'Fail on warnings' },
        },
      },
    },
  },
};

export async function handleValidateFrontendRoutes(
  args: Record<string, unknown>,
  config: Config
): Promise<string> {
  const input = ValidateFrontendRoutesInputSchema.parse(args);
  logger.info('Validating frontend routes', { scope: input.scope });

  const result = await validateFrontendRoutes(input, config);
  return formatResult(result, input);
}

async function validateFrontendRoutes(
  input: ValidateFrontendRoutesInput,
  config: Config
): Promise<FrontendRouteValidationResult> {
  const result: FrontendRouteValidationResult = {
    valid: true,
    registry: {
      exists: false,
      routeCount: 0,
      outdated: [],
    },
    apiClients: {
      total: 0,
      valid: 0,
      issues: [],
    },
    routes: {
      total: 0,
      orphaned: [],
      missing: [],
    },
    recommendations: [],
  };

  const { scope } = input;
  // Use explicit path if provided, otherwise fall back to config
  const projectRoot = input.path || config.smartstack.projectPath;
  const structure = await findSmartStackStructure(projectRoot);
  // Fallback to 'web' folder (not hardcoded to smartstack-web)
  const webPath = structure.web || path.join(projectRoot, 'web');

  // Discover backend NavRoutes
  const backendRoutes = await discoverBackendNavRoutes(structure);

  // Validate registry
  if (scope === 'all' || scope === 'registry') {
    await validateRegistry(webPath, backendRoutes, result);
  }

  // Validate API clients
  if (scope === 'all' || scope === 'api-clients') {
    await validateApiClients(webPath, backendRoutes, result);
  }

  // Validate React Router configuration
  if (scope === 'all' || scope === 'routes') {
    await validateRoutes(webPath, backendRoutes, result);
  }

  // Generate recommendations
  generateRecommendations(result);

  // Determine overall validity
  result.valid = result.apiClients.issues.filter(i => i.severity === 'error').length === 0 &&
    result.routes.missing.length === 0 &&
    result.registry.exists;

  return result;
}

async function discoverBackendNavRoutes(
  structure: Awaited<ReturnType<typeof findSmartStackStructure>>
): Promise<NavRouteEntry[]> {
  const routes: NavRouteEntry[] = [];
  const apiPath = structure.api;

  if (!apiPath) {
    return routes;
  }

  const controllerFiles = await glob('**/*Controller.cs', {
    cwd: apiPath,
    absolute: true,
    ignore: ['**/obj/**', '**/bin/**'],
  });

  for (const file of controllerFiles) {
    try {
      const content = await readText(file);
      const navRouteMatch = content.match(/\[NavRoute\s*\(\s*"([^"]+)"(?:\s*,\s*Suffix\s*=\s*"([^"]+)")?\s*\)\]/);

      if (navRouteMatch) {
        const navRoute = navRouteMatch[1];
        const suffix = navRouteMatch[2];
        const fullNavRoute = suffix ? `${navRoute}.${suffix}` : navRoute;

        const controllerMatch = path.basename(file).match(/(.+)Controller\.cs$/);
        const controllerName = controllerMatch ? controllerMatch[1] : 'Unknown';

        const methods: string[] = [];
        if (content.includes('[HttpGet]')) methods.push('GET');
        if (content.includes('[HttpPost]')) methods.push('POST');
        if (content.includes('[HttpPut]')) methods.push('PUT');
        if (content.includes('[HttpDelete]')) methods.push('DELETE');

        const permissions: string[] = [];
        const authorizeMatches = content.matchAll(/\[Authorize\s*\(\s*[^)]*Policy\s*=\s*"([^"]+)"/g);
        for (const match of authorizeMatches) {
          permissions.push(match[1]);
        }

        routes.push({
          navRoute: fullNavRoute,
          apiPath: `/api/${navRoute.replace(/\./g, '/')}${suffix ? `/${suffix}` : ''}`,
          webPath: `/${navRoute.replace(/\./g, '/')}${suffix ? `/${suffix}` : ''}`,
          permissions,
          controller: controllerName,
          methods,
        });
      }
    } catch {
      logger.debug(`Failed to parse controller: ${file}`);
    }
  }

  return routes;
}

async function validateRegistry(
  webPath: string,
  backendRoutes: NavRouteEntry[],
  result: FrontendRouteValidationResult
): Promise<void> {
  const registryPath = path.join(webPath, 'src', 'routes', 'navRoutes.generated.ts');

  if (!await fileExists(registryPath)) {
    result.registry.exists = false;
    result.recommendations.push('Run `scaffold_routes` to generate navRoutes.generated.ts');
    return;
  }

  result.registry.exists = true;

  try {
    const content = await readText(registryPath);

    // Count routes in registry
    const routeMatches = content.matchAll(/'([a-z.]+)':\s*\{/g);
    const registryRoutes = new Set<string>();
    for (const match of routeMatches) {
      registryRoutes.add(match[1]);
    }

    result.registry.routeCount = registryRoutes.size;

    // Check for outdated routes
    for (const backendRoute of backendRoutes) {
      if (!registryRoutes.has(backendRoute.navRoute)) {
        result.registry.outdated.push(backendRoute.navRoute);
      }
    }

    // Check for routes in registry that no longer exist in backend
    for (const registryRoute of registryRoutes) {
      if (!backendRoutes.find(r => r.navRoute === registryRoute)) {
        result.registry.outdated.push(`${registryRoute} (removed from backend)`);
      }
    }
  } catch {
    result.registry.exists = false;
  }
}

async function validateApiClients(
  webPath: string,
  backendRoutes: NavRouteEntry[],
  result: FrontendRouteValidationResult
): Promise<void> {
  const servicesPath = path.join(webPath, 'src', 'services', 'api');

  const clientFiles = await glob('**/*.ts', {
    cwd: servicesPath,
    absolute: true,
    ignore: ['**/index.ts'],
  });

  result.apiClients.total = clientFiles.length;

  for (const file of clientFiles) {
    try {
      const content = await readText(file);
      const relativePath = path.relative(webPath, file);

      // Check if client uses getRoute()
      const usesRegistry = content.includes("getRoute('") || content.includes('getRoute("');

      if (!usesRegistry) {
        // Check for hardcoded API paths
        const hardcodedMatch = content.match(/apiClient\.(get|post|put|delete)\s*[<(]\s*['"`]([^'"`]+)['"`]/);
        if (hardcodedMatch) {
          result.apiClients.issues.push({
            type: 'invalid-path',
            severity: 'warning',
            file: relativePath,
            message: `Hardcoded API path: ${hardcodedMatch[2]}`,
            suggestion: 'Use getRoute() from navRoutes.generated.ts instead',
          });
        }
      } else {
        // Validate the NavRoute reference
        const navRouteMatch = content.match(/getRoute\s*\(\s*['"`]([^'"`]+)['"`]\s*\)/);
        if (navRouteMatch) {
          const navRoute = navRouteMatch[1];
          const backendRoute = backendRoutes.find(r => r.navRoute === navRoute);

          if (!backendRoute) {
            result.apiClients.issues.push({
              type: 'missing-route',
              severity: 'error',
              file: relativePath,
              navRoute,
              message: `NavRoute "${navRoute}" not found in backend controllers`,
              suggestion: 'Verify the NavRoute path or update the backend controller',
            });
          } else {
            result.apiClients.valid++;
          }
        }
      }
    } catch {
      logger.debug(`Failed to parse API client: ${file}`);
    }
  }
}

async function validateRoutes(
  webPath: string,
  backendRoutes: NavRouteEntry[],
  result: FrontendRouteValidationResult
): Promise<void> {
  const routesPath = path.join(webPath, 'src', 'routes', 'index.tsx');

  if (!await fileExists(routesPath)) {
    result.routes.total = 0;
    result.routes.missing = backendRoutes.map(r => r.navRoute);
    return;
  }

  try {
    const content = await readText(routesPath);

    // Extract paths from React Router configuration
    const pathMatches = content.matchAll(/path:\s*['"`]([^'"`]+)['"`]/g);
    const frontendPaths = new Set<string>();
    for (const match of pathMatches) {
      frontendPaths.add(match[1]);
    }

    result.routes.total = frontendPaths.size;

    // Check for missing frontend routes
    for (const backendRoute of backendRoutes) {
      const webPath = backendRoute.webPath.replace(/^\//, '');
      const parts = webPath.split('/');

      // Check if any part of the path is defined
      let found = false;
      for (const pathPart of parts) {
        if (frontendPaths.has(pathPart)) {
          found = true;
          break;
        }
      }

      if (!found && parts.length > 0) {
        result.routes.missing.push(backendRoute.navRoute);
      }
    }

    // Check for orphaned frontend routes (routes without backend)
    for (const frontendPath of frontendPaths) {
      if (frontendPath === '*' || frontendPath === '' || frontendPath.startsWith(':')) {
        continue;
      }

      const matchingBackend = backendRoutes.find(r =>
        r.webPath.includes(frontendPath) || r.navRoute.includes(frontendPath)
      );

      if (!matchingBackend) {
        result.routes.orphaned.push(frontendPath);
      }
    }
  } catch {
    result.routes.total = 0;
    result.routes.missing = backendRoutes.map(r => r.navRoute);
  }
}

function generateRecommendations(result: FrontendRouteValidationResult): void {
  if (!result.registry.exists) {
    result.recommendations.push('Run `scaffold_routes source="controllers"` to generate route registry');
  } else if (result.registry.outdated.length > 0) {
    result.recommendations.push(`Registry is outdated: ${result.registry.outdated.length} routes need sync. Run \`scaffold_routes\``);
  }

  if (result.apiClients.issues.length > 0) {
    const hardcoded = result.apiClients.issues.filter(i => i.type === 'invalid-path').length;
    const missing = result.apiClients.issues.filter(i => i.type === 'missing-route').length;

    if (hardcoded > 0) {
      result.recommendations.push(`${hardcoded} API clients use hardcoded paths. Migrate to getRoute()`);
    }
    if (missing > 0) {
      result.recommendations.push(`${missing} API clients reference non-existent NavRoutes`);
    }
  }

  if (result.routes.missing.length > 0) {
    result.recommendations.push(`${result.routes.missing.length} backend routes have no frontend counterpart`);
  }

  if (result.routes.orphaned.length > 0) {
    result.recommendations.push(`${result.routes.orphaned.length} frontend routes have no backend NavRoute`);
  }

  if (result.valid && result.recommendations.length === 0) {
    result.recommendations.push('All routes are synchronized between frontend and backend');
  }
}

function formatResult(
  result: FrontendRouteValidationResult,
  _input: ValidateFrontendRoutesInput
): string {
  const lines: string[] = [];

  const statusIcon = result.valid ? '✅' : '❌';
  lines.push(`# Frontend Route Validation ${statusIcon}`);
  lines.push('');

  lines.push('## Summary');
  lines.push('');
  lines.push(`| Metric | Value |`);
  lines.push(`|--------|-------|`);
  lines.push(`| Valid | ${result.valid ? 'Yes' : 'No'} |`);
  lines.push(`| Registry Exists | ${result.registry.exists ? 'Yes' : 'No'} |`);
  lines.push(`| Registry Routes | ${result.registry.routeCount} |`);
  lines.push(`| API Clients | ${result.apiClients.valid}/${result.apiClients.total} valid |`);
  lines.push(`| Frontend Routes | ${result.routes.total} |`);
  lines.push(`| Missing Routes | ${result.routes.missing.length} |`);
  lines.push(`| Orphaned Routes | ${result.routes.orphaned.length} |`);
  lines.push('');

  if (result.registry.outdated.length > 0) {
    lines.push('## Outdated Registry Entries');
    lines.push('');
    for (const route of result.registry.outdated) {
      lines.push(`- \`${route}\``);
    }
    lines.push('');
  }

  if (result.apiClients.issues.length > 0) {
    lines.push('## API Client Issues');
    lines.push('');
    for (const issue of result.apiClients.issues) {
      const icon = issue.severity === 'error' ? '❌' : '⚠️';
      lines.push(`### ${icon} ${issue.file}`);
      lines.push('');
      lines.push(`- **Type**: ${issue.type}`);
      lines.push(`- **Message**: ${issue.message}`);
      if (issue.navRoute) {
        lines.push(`- **NavRoute**: \`${issue.navRoute}\``);
      }
      lines.push(`- **Suggestion**: ${issue.suggestion}`);
      lines.push('');
    }
  }

  if (result.routes.missing.length > 0) {
    lines.push('## Missing Frontend Routes');
    lines.push('');
    lines.push('These backend NavRoutes have no corresponding frontend route:');
    lines.push('');
    for (const route of result.routes.missing) {
      lines.push(`- \`${route}\``);
    }
    lines.push('');
  }

  if (result.routes.orphaned.length > 0) {
    lines.push('## Orphaned Frontend Routes');
    lines.push('');
    lines.push('These frontend routes have no corresponding backend NavRoute:');
    lines.push('');
    for (const route of result.routes.orphaned) {
      lines.push(`- \`${route}\``);
    }
    lines.push('');
  }

  lines.push('## Recommendations');
  lines.push('');
  for (const rec of result.recommendations) {
    lines.push(`- ${rec}`);
  }
  lines.push('');

  lines.push('## Commands');
  lines.push('');
  lines.push('```bash');
  lines.push('# Regenerate route registry');
  lines.push('scaffold_routes source="controllers"');
  lines.push('');
  lines.push('# Generate API client for a specific NavRoute');
  lines.push('scaffold_api_client navRoute="platform.administration.users" name="User"');
  lines.push('```');

  return lines.join('\n');
}
